import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core'

import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar'
import {
  MatBottomSheet,
  MatBottomSheetModule,
} from '@angular/material/bottom-sheet'
import {
  Subject,
  debounceTime,
  takeUntil,
  firstValueFrom,
  Subscription,
  timer,
  switchMap,
  filter,
} from 'rxjs'
import { ActivatedRoute, NavigationStart, Router } from '@angular/router'
import { TranslationService } from '../../../../services/translation.service'
import {
  FprPlasticRow,
  ReportResponse,
  FprRows,
  FprResinRow,
  ExemptionCheckResponse,
  ImportReportResponse,
  FprReport,
} from '../../../../types/reports'
import { FprTemplate } from '../../../../types/fprTemplates'
import { ReportService } from '../../../../services/report.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { ErrorService } from '../../../../services/error.service'
import { DialogService } from '../../../../services/dialog.service'
import { ApiError } from '../../../../types/apiError'
import { Dialog } from '@angular/cdk/dialog'
import {
  SNACKBAR_DEFAULT_DURATION_MS,
  SNACKBAR_IMPORT_DURATION_MS,
} from '../../../../constants/common'
import { FprImportSheetComponent } from './fpr-import-sheet/fpr-import-sheet.component'
import { FprTemplatesService } from '../../../../services/fprTemplate.service'
import { MatTabsModule } from '@angular/material/tabs'
import { PlasticsComponent } from './plastics/plastics.component'
import { ResinsComponent } from './resins/resins.component'
import { MatSelectModule } from '@angular/material/select'
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../shared/confirm-dialog/confirm-dialog.component'
import { MissingProductsComponent } from './missing-products/missing-products.component'
import { ExternalRowSheetComponent } from './external-row-sheet/external-row-sheet.component'
import { MatTooltipModule } from '@angular/material/tooltip'
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete'
import { MatChipsModule } from '@angular/material/chips'
import { COMMA, ENTER } from '@angular/cdk/keycodes'

import { ProductTagService } from '../../../../services/productTag.service'

@Component({
  selector: 'app-fpr-report',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSnackBarModule,
    MatBottomSheetModule,
    MatTabsModule,
    PlasticsComponent,
    ResinsComponent,
    MatSelectModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatChipsModule,
  ],
  templateUrl: './fpr-report.component.html',
  styleUrls: ['./fpr-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FprReportComponent implements OnInit, OnDestroy {
  translationService = inject(TranslationService)
  private reportService = inject(ReportService)
  private spinnerService = inject(SpinnerService)
  private errorService = inject(ErrorService)
  private dialogService = inject(DialogService)
  private fprTemplateService = inject(FprTemplatesService)
  private productTagService = inject(ProductTagService)
  private dialog = inject(Dialog)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private snackBar = inject(MatSnackBar)
  private bottomSheet = inject(MatBottomSheet)
  private cdr = inject(ChangeDetectorRef)

  templates: FprTemplate[] = []
  productTags: string[] = []
  filteredProductTags: string[] = []

  readonly tagSeparatorKeysCodes = [ENTER, COMMA] as const
  readonly reportTagsCtrl = new FormControl<string[]>([], { nonNullable: true })
  readonly reportTagSearchCtrl = new FormControl('', { nonNullable: true })

  private inactivityTimeoutId?: ReturnType<typeof setTimeout>
  private readonly INACTIVITY_LIMIT_MS = 2 * 60 * 1000
  private boundMousemoveHandler?: (e: Event) => void
  private boundKeydownHandler?: (e: Event) => void
  private boundVisibilityHandler?: (e: Event) => void
  private hasPollingStarted = false
  private wasPaused = false

  reportYearCtrl = new FormControl<number | null>(null, {
    validators: [Validators.required],
  })
  reportIdFromRoute: string | null = null

  currentReportId: string | null = null
  currentReportName: string | null = null
  isEditMode = false
  isPublishing = false

  // Rows relayed to children
  plasticRows: FprPlasticRow[] = []
  resinRows: FprResinRow[] = []

  // Debounced patch queues from children
  private plasticPatchSignal$ = new Subject<void>()
  private resinPatchSignal$ = new Subject<void>()
  private pendingPlasticPatchMap = new Map<string, Partial<FprPlasticRow>>()
  private pendingResinPatchMap = new Map<string, Partial<FprResinRow>>()

  private readonly destroyed$ = new Subject<void>()
  private pollingSub?: Subscription
  // Tracks whether the plastics tab currently has unsaved edits.
  private isPlasticsEditing = false
  // Tracks whether the resins tab currently has unsaved edits.
  private isResinsEditing = false

  constructor() {
    // Stop polling on route navigation to prevent background activity on other pages
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.stopPolling(true, true) // suppress toast, remove listeners
      })
  }

  async ngOnInit(): Promise<void> {
    // Detect edit mode from route param ':id'
    const id = this.route.snapshot.paramMap.get('id')
    this.reportIdFromRoute = id
    this.isEditMode = !!id
    if (this.isEditMode) {
      // Set current report id for downstream actions (autosave, download, etc.)
      this.currentReportId = id
      this.reportYearCtrl.disable({ emitEvent: false })
      await this.loadRowsForEdit(id as string)
      this.startPolling(id as string)
    }

    try {
      this.templates = await firstValueFrom(this.fprTemplateService.getAll())
      this.productTags = await firstValueFrom(
        this.productTagService.getByCompanyId(),
      )

      this.filteredProductTags = [...this.productTags]
    } catch (err) {
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }

    this.reportTagSearchCtrl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        const search = value.trim().toLowerCase()
        this.filteredProductTags = this.productTags.filter((tag) =>
          tag.toLowerCase().includes(search),
        )
      })

    // Debounce child patch streams before flushing to API
    this.plasticPatchSignal$
      .pipe(debounceTime(800), takeUntil(this.destroyed$))
      .subscribe(() => this.flushPlasticPatches())

    this.resinPatchSignal$
      .pipe(debounceTime(800), takeUntil(this.destroyed$))
      .subscribe(() => this.flushResinPatches())
  }

  ngOnDestroy(): void {
    // Fully stop polling and remove any global listeners
    this.stopPolling(true, true)
    this.destroyed$.next()
    this.destroyed$.complete()
  }

  private startPolling(reportId: string) {
    // Stop any existing loop, but suppress the pause toast when (re)starting
    this.stopPolling(true, true)

    // Start polling every 5 seconds
    this.pollingSub = timer(5000, 5000)
      .pipe(switchMap(() => this.reportService.getReport(reportId)))
      .subscribe({
        next: (resp) => {
          this.applyServerReportMeta(resp?.report ?? null)
          const rows: FprRows = resp?.rows ?? { plastics: [], resins: [] }
          this.applyServerRows(rows)
        },
        error: () => {},
      })

    // Begin/reset the idle timer; inactivity will pause polling
    this.resetInactivityTimer(reportId)

    // Attach activity/visibility listeners once; they call resetInactivityTimer or stop/restart polling
    if (!this.boundMousemoveHandler) {
      this.boundMousemoveHandler = () => this.resetInactivityTimer(reportId)
    }
    if (!this.boundKeydownHandler) {
      this.boundKeydownHandler = () => this.resetInactivityTimer(reportId)
    }
    if (!this.boundVisibilityHandler) {
      this.boundVisibilityHandler = () => {
        if (document.visibilityState === 'hidden') {
          this.stopPolling() // pause while hidden
        } else if (document.visibilityState === 'visible') {
          this.startPolling(reportId) // resume loop
          this.loadRowsForEdit(reportId) // immediate refresh
        }
      }
    }
    window.addEventListener('mousemove', this.boundMousemoveHandler!)
    window.addEventListener('keydown', this.boundKeydownHandler!)
    document.addEventListener('visibilitychange', this.boundVisibilityHandler!)

    // Only show “resumed” if we had previously paused
    if (this.hasPollingStarted && this.wasPaused) {
      this.snackBar.open(
        this.translationService.getAutoUpdatesResumedText(),
        '',
        { duration: 3000 },
      )
    }
    this.hasPollingStarted = true
    this.wasPaused = false
  }

  private stopPolling(suppressToast?: boolean, removeListeners?: boolean) {
    const wasActive = !!this.pollingSub

    // Stop the polling interval
    this.pollingSub?.unsubscribe()
    this.pollingSub = undefined

    // Clear inactivity timer
    if (this.inactivityTimeoutId) {
      clearTimeout(this.inactivityTimeoutId)
      this.inactivityTimeoutId = undefined
    }

    // Optionally detach global listeners (used on route nav away)
    if (removeListeners) {
      if (this.boundMousemoveHandler) {
        window.removeEventListener('mousemove', this.boundMousemoveHandler)
        this.boundMousemoveHandler = undefined
      }
      if (this.boundKeydownHandler) {
        window.removeEventListener('keydown', this.boundKeydownHandler)
        this.boundKeydownHandler = undefined
      }
      if (this.boundVisibilityHandler) {
        document.removeEventListener(
          'visibilitychange',
          this.boundVisibilityHandler,
        )
        this.boundVisibilityHandler = undefined
      }
    }

    // Show “paused” only when stopping an active loop and not suppressed
    if (wasActive && !suppressToast) {
      this.snackBar.open(
        this.translationService.getAutoUpdatesPausedText(),
        '',
        { duration: 3000 },
      )
      this.wasPaused = true
    }
  }

  private resetInactivityTimer(reportId: string) {
    if (this.inactivityTimeoutId) {
      clearTimeout(this.inactivityTimeoutId)
    }
    // If polling was stopped (e.g., due to idle), restart it on activity when tab is visible
    if (
      !this.pollingSub &&
      document.visibilityState === 'visible' &&
      this.isEditMode
    ) {
      // immediate refresh then restart the interval
      this.loadRowsForEdit(reportId)
      this.startPolling(reportId)
      return
    }
    this.inactivityTimeoutId = setTimeout(() => {
      this.stopPolling()
    }, this.INACTIVITY_LIMIT_MS)
  }

  // Merge server rows into relayed fields for children
  private applyServerRows(rows: FprRows) {
    // Do not overwrite rows while the plastics or resins tabs have unsaved edits.
    if (this.isPlasticsEditing || this.isResinsEditing) {
      return
    }
    this.setRows(rows)
  }

  private applyServerReportMeta(
    report:
      | (Partial<FprReport> & {
          id: string
          name: string
        })
      | null,
  ): void {
    if (!report) {
      return
    }

    this.currentReportId = report.id
    this.currentReportName = report.name

    if (report.year != null) {
      this.reportYearCtrl.setValue(report.year, { emitEvent: false })
    }

    if (Array.isArray(report.tags)) {
      this.reportTagsCtrl.setValue(report.tags, { emitEvent: false })
    }
    this.cdr.markForCheck()
  }

  // Generate rows for the fixed year and enter edit mode
  async onGenerateReport(): Promise<void> {
    const year = this.reportYearCtrl.value
    if (year == null) {
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getReportsText(),
        this.translationService.getYearRequiredText(),
      )
      return
    }
    try {
      this.spinnerService.startSpinner()
      const resp: ReportResponse = await firstValueFrom(
        this.reportService.generateReportRows(year, this.reportTagsCtrl.value),
      )
      const rows: FprRows = resp.rows
      const report = resp.report
      this.applyServerReportMeta(report ?? null)

      if (report && (rows.plastics.length || rows.resins.length)) {
        this.currentReportId = report.id
        this.currentReportName = report.name
        this.isEditMode = true
        // navigate to the edit route for the new report id
        this.router.navigate(['/admin/reports/fpr', report.id, 'edit'], {
          replaceUrl: true,
        })
      } else {
        this.dialogService.showDialog(
          this.dialog,
          this.translationService.getReportsText(),
          this.translationService.getNoProductsForYearGenerationText(),
        )

        this.spinnerService.stopSpinner()
        return
      }

      this.setRows(rows)
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  private async loadRowsForEdit(reportId: string): Promise<void> {
    this.spinnerService.startSpinner()
    try {
      const resp = await firstValueFrom(this.reportService.getReport(reportId))
      this.applyServerReportMeta(resp.report ?? null)
      this.setRows(resp.rows)
    } catch (err) {
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    } finally {
      this.spinnerService.stopSpinner()
    }
  }

  async onSave(): Promise<void> {
    if (!this.currentReportId) return

    try {
      this.spinnerService.startSpinner()

      // ensure latest edits are pushed to Redis
      await this.flushPlasticPatches()
      await this.flushResinPatches()

      // ask backend to persist current drafts to DB
      await firstValueFrom(this.reportService.saveReport(this.currentReportId))

      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getReportsText(),
        this.translationService.getReportSavedSuccessfullyText(),
      )
    } catch (err) {
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    } finally {
      this.spinnerService.stopSpinner()
    }
  }

  async onPublish(): Promise<void> {
    if (!this.currentReportId || this.isPublishing) return
    this.isPublishing = true
    const reportName = this.currentReportName

    try {
      // Make sure latest edits are in Redis before checking/publishing
      await this.flushPlasticPatches()
      await this.flushResinPatches()

      // perform the exemption check
      const check = (await firstValueFrom(
        this.reportService.checkExemption(this.currentReportId),
      )) as ExemptionCheckResponse

      // If it’s under the threshold, ask the user to confirm
      if (check.qualifies) {
        const dialogData: ConfirmDialogData = {
          header: this.translationService.getConfirmActionHeaderText(),
          message: this.translationService.getPublishConfirmBelowThresholdText(
            check.totalKg,
            check.plasticsKg,
            check.resinsKg,
            check.threshold,
          ),
          onCancel: () => {},
        }
        const dialogRef = this.dialog.open<boolean>(ConfirmDialogComponent, {
          data: dialogData,
        })
        const confirmed = await firstValueFrom(dialogRef.closed)
        if (!confirmed) {
          this.isPublishing = false
          this.cdr.markForCheck()
          return
        }
      }

      this.spinnerService.startSpinner()

      // Ensure latest edits are pushed before publishing
      await this.flushPlasticPatches()
      await this.flushResinPatches()

      // Ask backend to start background publishing (returns 202 Accepted)
      await firstValueFrom(
        this.reportService.publishReport(this.currentReportId),
      )

      // Feedback then navigate away
      this.snackBar.open(
        `“${reportName}” ${this.translationService.getPublishedText()}`,
        '',
        { duration: SNACKBAR_DEFAULT_DURATION_MS },
      )
      this.router.navigate(['/admin/reports'])
    } catch (err) {
      // Any error (including exemption-check) blocks publishing
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    } finally {
      this.isPublishing = false
      this.cdr.markForCheck()

      this.spinnerService.stopSpinner()
    }
  }

  openImportSheet(): void {
    const year = this.reportYearCtrl.value

    // When creating a new report, a year must be selected first.
    if (!this.isEditMode && year == null) {
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getReportsText(),
        this.translationService.getYearRequiredText?.() ??
          this.translationService.getYearText(),
      )
      return
    }

    const ref = this.bottomSheet.open(FprImportSheetComponent, {
      data: {
        year,
        templates: this.templates,
        reportId: this.currentReportId,
        isEditMode: this.isEditMode,
        tags: this.reportTagsCtrl.value,
      },
    })

    ref.afterDismissed().subscribe(async (result?: ImportReportResponse) => {
      if (!result) {
        // If nothing was returned but we're already editing a report, reload from server
        if (this.isEditMode && this.currentReportId) {
          await this.loadRowsForEdit(this.currentReportId)
        }
        return
      }

      // Case 1: creating a new report from the upload
      if (!this.isEditMode && result.report) {
        this.currentReportId = result.report.id
        this.currentReportName = result.report.name
        this.isEditMode = true

        this.router.navigate(['/admin/reports/fpr', result.report.id, 'edit'], {
          replaceUrl: true,
        })

        // Set initial rows from the response, then start polling
        if (result.rows) {
          this.setRows(result.rows)
        }

        let message: string
        const summary = result.missingProducts
        if (summary) {
          message = this.translationService.getSpreadsheetImportSummaryText(
            summary.reportedCount,
            summary.totalEligible,
          )
        } else {
          message = this.translationService.getSpreadsheetImportSummaryText(
            0,
            0,
          )
        }

        this.snackBar.open(message, '', {
          duration: SNACKBAR_IMPORT_DURATION_MS,
        })

        this.startPolling(result.report.id)
        return
      }

      // Case 2: updating an existing report with another upload
      if (this.isEditMode && this.currentReportId) {
        await this.loadRowsForEdit(this.currentReportId)

        let message: string
        const summary = result.missingProducts
        if (summary) {
          message = this.translationService.getSpreadsheetImportSummaryText(
            summary.reportedCount,
            summary.totalEligible,
          )
        } else {
          message = this.translationService.getSpreadsheetImportSummaryText(
            0,
            0,
          )
        }

        this.snackBar.open(message, '', {
          duration: SNACKBAR_IMPORT_DURATION_MS,
        })
      }
    })
  }

  /**
   * Opens the missing products bottom sheet for the current report.
   * Only available when a report already exists.
   * After hydration, forces a one-off refresh of the current rows.
   */
  async openMissingProducts(): Promise<void> {
    if (!this.currentReportId) {
      return
    }

    const year = this.reportYearCtrl.value

    const ref = this.bottomSheet.open(MissingProductsComponent, {
      data: {
        reportId: this.currentReportId,
        year,
      },
    })

    try {
      const result = await firstValueFrom(ref.afterDismissed())
      if (result?.hydrated && this.currentReportId) {
        await this.loadRowsForEdit(this.currentReportId)
      }
    } catch {
      // if the sheet closes unexpectedly, we simply skip the refresh
    }
  }

  async onDownloadRawData(): Promise<void> {
    if (!this.currentReportId || !this.currentReportName) return

    try {
      this.spinnerService.startSpinner()

      const res = await firstValueFrom(
        this.reportService.downloadRaw(this.currentReportId),
      )

      const blob = res.body!
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ext = this.currentReportName.endsWith('.xlsx') ? '.xlsx' : ''
      const base = ext
        ? this.currentReportName.slice(0, -ext.length)
        : this.currentReportName
      a.download = `${base}_raw${ext || '.xlsx'}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)

      this.translationService.getDownloadStartedText?.()
    } catch (err) {
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    } finally {
      this.spinnerService.stopSpinner()
    }
  }

  // Set both plastics and resins from FprRows
  private setRows(rows: FprRows): void {
    this.plasticRows = (rows?.plastics ?? []).slice()
    this.resinRows = (rows?.resins ?? []).slice()
    this.cdr.markForCheck()
  }

  // Intake patches from plastics child
  onPlasticsPatches(
    patches: Array<{ id: string; fields: Partial<FprPlasticRow> }>,
  ): void {
    for (const p of patches) {
      const prev = this.pendingPlasticPatchMap.get(p.id) ?? {}
      this.pendingPlasticPatchMap.set(p.id, { ...prev, ...p.fields })
    }
    this.plasticPatchSignal$.next()
  }

  // Intake patches from resins child
  onResinsPatches(
    patches: Array<{ id: string; fields: Partial<FprResinRow> }>,
  ): void {
    for (const p of patches) {
      const prev = this.pendingResinPatchMap.get(p.id) ?? {}
      this.pendingResinPatchMap.set(p.id, { ...prev, ...p.fields })
    }
    this.resinPatchSignal$.next()
  }

  // Flush debounced plastic patches
  private async flushPlasticPatches(): Promise<void> {
    if (!this.currentReportId || this.pendingPlasticPatchMap.size === 0) return
    const patches = Array.from(this.pendingPlasticPatchMap.entries()).map(
      ([id, fields]) => ({ id, fields }),
    )
    this.pendingPlasticPatchMap.clear()
    try {
      await firstValueFrom(
        this.reportService.patchPlasticRows(this.currentReportId, patches),
      )
    } catch (err) {
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  // Flush debounced resin patches
  private async flushResinPatches(): Promise<void> {
    if (!this.currentReportId || this.pendingResinPatchMap.size === 0) return
    const patches = Array.from(this.pendingResinPatchMap.entries()).map(
      ([id, fields]) => ({ id, fields }),
    )
    this.pendingResinPatchMap.clear()
    try {
      await firstValueFrom(
        this.reportService.patchResinRows(this.currentReportId, patches),
      )
    } catch (err) {
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  // Called by the plastics child whenever editing state changes.
  onPlasticsEditing(editing: boolean): void {
    this.isPlasticsEditing = editing
  }

  // Called by the resins child whenever editing state changes.
  onResinsEditing(editing: boolean): void {
    this.isResinsEditing = editing
  }

  openAddProvisionalRow(): void {
    if (!this.currentReportId) {
      return
    }

    const existingNonProvisionalSkus = Array.from(
      new Set(
        (this.plasticRows ?? [])
          .filter((r) => !r.isProvisional && !r.isTotalRow)
          .map((r) => (r.sku ?? '').trim())
          .filter((sku) => sku !== ''),
      ),
    )

    const ref = this.bottomSheet.open(ExternalRowSheetComponent, {
      data: {
        reportId: this.currentReportId,
        year: this.reportYearCtrl.value,
        existingNonProvisionalSkus,
      },
    })

    ref.afterDismissed().subscribe(async (result) => {
      if (!result || !this.currentReportId) {
        return
      }

      // After a provisional row is added, reload current drafts
      await this.loadRowsForEdit(this.currentReportId)
    })
  }

  addReportTag(event: MatAutocompleteSelectedEvent): void {
    const value = (event.option.value ?? '').trim()
    if (!value) {
      return
    }

    const current = this.reportTagsCtrl.value
    if (!current.includes(value)) {
      this.reportTagsCtrl.setValue([...current, value])
    }

    this.reportTagSearchCtrl.setValue('')
    this.filteredProductTags = [...this.productTags]
  }

  removeReportTag(tagToRemove: string): void {
    this.reportTagsCtrl.setValue(
      this.reportTagsCtrl.value.filter((tag) => tag !== tagToRemove),
    )
  }
}
